/**
 * app.ts — AppShell
 *
 * Responsible for the top-level DOM structure:
 *   #clouds         – animated / decorative background layer
 *   #book-viewport  – container the renderer renders into
 *   #ui-chrome      – navigation controls (prev/next, fullscreen)
 *
 * Extension points
 * ----------------
 * - Call `shell.attachRenderer(renderer)` to wire up the BookRenderer
 *   once it is implemented (see src/book/renderer.ts).
 * - The `#book-viewport` element is exposed via `shell.viewport` for
 *   the renderer to mount into.
 */

import type { BookRenderer } from "./book/renderer";

export class AppShell {
  private readonly el: HTMLElement;
  private renderer: BookRenderer | null = null;

  /** The element the renderer should mount into. */
  readonly viewport: HTMLElement;

  constructor(root: HTMLElement) {
    this.el = root;
    this.viewport = document.createElement("div");
    this.viewport.id = "book-viewport";
    this.viewport.setAttribute("aria-label", "Book pages");
  }

  mount(): void {
    this.el.innerHTML = "";
    this.el.append(
      this.buildClouds(),
      this.viewport,
      this.buildChrome(),
    );

    document.addEventListener("keydown", this.handleKey.bind(this));
    this.el.dataset.ready = "true";
  }

  /** Wire the renderer after the content workstream is ready. */
  attachRenderer(renderer: BookRenderer): void {
    this.renderer = renderer;
    renderer.mount(this.viewport);
  }

  // ── private helpers ──────────────────────────────────────────────────────

  private buildClouds(): HTMLElement {
    const layer = document.createElement("div");
    layer.id = "clouds";
    layer.setAttribute("aria-hidden", "true");
    // Individual cloud <span>s are generated here so CSS animations can be
    // staggered via --cloud-index custom property.
    for (let i = 0; i < 6; i++) {
      const cloud = document.createElement("span");
      cloud.className = "cloud";
      cloud.style.setProperty("--cloud-index", String(i));
      layer.appendChild(cloud);
    }
    return layer;
  }

  private buildChrome(): HTMLElement {
    const chrome = document.createElement("nav");
    chrome.id = "ui-chrome";
    chrome.setAttribute("aria-label", "Book navigation");

    const prev = this.createBtn("btn-prev", "◀", "Previous page", () =>
      this.renderer?.prevPage(),
    );
    const next = this.createBtn("btn-next", "▶", "Next page", () =>
      this.renderer?.nextPage(),
    );
    const fs = this.createBtn("btn-fullscreen", "⛶", "Toggle fullscreen", () =>
      this.toggleFullscreen(),
    );

    chrome.append(prev, next, fs);
    return chrome;
  }

  private createBtn(
    id: string,
    label: string,
    title: string,
    handler: () => void,
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.id = id;
    btn.textContent = label;
    btn.title = title;
    btn.type = "button";
    btn.addEventListener("click", handler);
    return btn;
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      this.el.requestFullscreen().catch(console.warn);
    } else {
      document.exitFullscreen().catch(console.warn);
    }
  }

  private handleKey(e: KeyboardEvent): void {
    if (e.key === "ArrowRight" || e.key === "PageDown")
      this.renderer?.nextPage();
    if (e.key === "ArrowLeft" || e.key === "PageUp") this.renderer?.prevPage();
    if (e.key === "f" || e.key === "F") this.toggleFullscreen();
  }
}
